export function getFieldValue(fieldName: string, taskStep: TaskStep, dataObject: any): any {
    // Sucht rekursiv nach einem Feldnamen in einer Liste von Feldern
    function findField(fieldName: string, fields: TaskStepField[]): TaskStepField | null {
        for (const field of fields) {
            if (field.name === fieldName) {
                return field
            } else if ("subFields" in field) {
                const subField = findField(fieldName, (field as TaskStepFieldObject).subFields)
                if (subField) {
                    return subField
                }
            }
        }
        return null
    }

    // Flache Liste aller Felder in allen Seiten des TaskSteps
    const fields = taskStep.pages.flatMap((page) => page.subFields)
    const field = findField(fieldName, fields)

    if (!field) {
        return null
    }

    // Erstelle den vollständigen Pfad zum Feld in der Datenbank
    const fullPath = `${taskStep.pathInDb}.${field.pathInDb}`.split(".").filter(Boolean)
    let value = dataObject

    // Navigiere durch das Datenobjekt basierend auf dem erstellten Pfad
    // kann aktuell nur flache Objekte verarbeiten, keine Arrays
    for (const part of fullPath) {
        if (value && typeof value === "object" && part in value) {
            value = value[part]
        } else {
            return undefined
        }
    }

    return value
}

/**
 * Angebots Assistent - Rule Engine
 * Resolve dependsOn property rules
 */

// ==	equals
// !=	does not equal
// <	is less than
// >	is greater than
// <=	is less than or equal to
// >=	is greater than or equal to
// in	is in
// !in	is not in

export class RuleEngine {
    public resolve(
        dependsOn: TaskStepDependsOnRule[],
        step: TaskStep,
        task: TaskOilSpillRemoval | TaskTowing
    ): boolean {
        return this.resolveAndGroup(dependsOn, step, task)
    }

    public resolveAndGroup(
        dependsOn: TaskStepDependsOnObject[],
        step: TaskStep,
        task: TaskOilSpillRemoval | TaskTowing
    ): boolean {
        if (dependsOn && dependsOn.length) {
            for (let i = 0; i < dependsOn.length; i++) {
                const dependsOnObj = dependsOn[i]
                if ("and" in dependsOnObj) {
                    if (!this.resolveAndGroup(dependsOnObj.and, step, task)) return false
                } else if ("or" in dependsOnObj) {
                    if (!this.resolveOrGroup(dependsOnObj.or, step, task)) return false
                } else {
                    let value = this.getValueByFieldName(dependsOnObj.name, step, task)
                    if (!this.resolveRule(dependsOnObj, step, task)) return false
                }
            }
        }
        return true
    }

    public resolveOrGroup(
        dependsOn: TaskStepDependsOnObject[],
        step: TaskStep,
        task: TaskOilSpillRemoval | TaskTowing
    ): boolean {
        if (dependsOn && dependsOn.length) {
            for (let i = 0; i < dependsOn.length; i++) {
                const dependsOnObj = dependsOn[i]
                if ("and" in dependsOnObj) {
                    if (this.resolveAndGroup(dependsOnObj.and, step, task)) return true
                } else if ("or" in dependsOnObj) {
                    if (this.resolveOrGroup(dependsOnObj.or, step, task)) return true
                } else {
                    if (this.resolveRule(dependsOnObj, step, task)) return true
                }
            }
        }
        return false
    }

    private getDBPathByFieldName(key: string, object: Object): any {
        return value
    }

    private resolveRule(
        dependsOn: TaskStepDependsOnRule,
        step: TaskStep,
        task: TaskOilSpillRemoval | TaskTowing
    ): boolean {
        for (let i = 0; i < dependsOn.rules.length; i++) {
            let rule = dependsOn.rules[i]

            let methodName = rule.condition
            let value = getFieldValue()

            if (rule.condition && typeof this[methodName] === "function") {
                if (!this[rule.condition](rule, value)) {
                    return false
                }
            } else {
                if (!this.isEqual(rule, step, task)) {
                    return false
                }
            }
        }

        return true
    }

    private isEmpty(rule: TaskStepDependsOnRuleObject, value: any): boolean {
        if (Array.isArray(value)) {
            return value.length === 0
        }

        return !value
    }

    private isNotEmpty(rule: TaskStepDependsOnRuleObject, value: any): boolean {
        if (Array.isArray(value)) {
            return value.length > 0
        }

        return value
    }

    private isEqual(rule: TaskStepDependsOnRuleObject, value: any): boolean {
        return rule.value === value
    }

    private isNotEqual(rule: TaskStepDependsOnRuleObject, value: any): boolean {
        return rule.value !== value
    }

    private isLessThan(rule: TaskStepDependsOnRuleObject, value: any): boolean {
        return value < rule.value
    }

    private isGreaterThan(rule: TaskStepDependsOnRuleObject, value: any): boolean {
        return value > rule.value
    }

    private isLessThanOrEqualTo(rule: TaskStepDependsOnRuleObject, value: any): boolean {
        return value <= rule.value
    }

    private isGreaterThanOrEqualTo(rule: TaskStepDependsOnRuleObject, value: any): boolean {
        return value >= rule.value
    }

    private isIn(rule: TaskStepDependsOnRuleObject, value: any): boolean {
        return value && value.includes(rule.value)
    }

    private isNotIn(rule: TaskStepDependsOnRuleObject, value: any): boolean {
        return value && !value.includes(rule.value)
    }
}
